import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { submitLead } from '../utils/submitLead';
import { isValidEmail, isValidUsPhone, limitUsPhone } from '../utils/leadFormValidation';

const steps = ['Development', 'Revenue', 'Marketing', 'Contact', 'Results'];

const initialValues = {
  developmentCost: '',
  maintenanceCost: '',
  complexity: '',
  platform: '',
  monetization: '',
  users: '',
  arpu: '',
  growth: '',
  marketing: '',
  cac: '',
  retention: '',
  conversion: '',
  name: '',
  email: '',
  phone: '',
};

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const number = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const percent = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function calculate(values) {
  const developmentCost = Number(values.developmentCost || 0);
  const maintenance = Number(values.maintenanceCost || 0);
  const marketing = Number(values.marketing || 0);
  const monthlyUsers = Number(values.users || 0);
  const arpu = Number(values.arpu || 0);
  const growthRate = Number(values.growth || 0);
  const growth = growthRate / 100;
  const activeUsers = [monthlyUsers, monthlyUsers * (1 + growth), monthlyUsers * (1 + growth) ** 2];
  const yearlyCosts = [developmentCost + maintenance * 12 + marketing * 12, maintenance * 12 + marketing * 12, maintenance * 12 + marketing * 12];
  const years = activeUsers.map((users, index) => {
    const monthlyRevenue = users * arpu;
    const annualRevenue = monthlyRevenue * 12;
    const annualCosts = yearlyCosts[index];
    const profit = annualRevenue - annualCosts;
    return { activeUsers: index ? Math.round(users) : users, monthlyRevenue, annualRevenue, annualCosts, profit, roi: 0 };
  });

  const revenue = years.reduce((sum, year) => sum + year.annualRevenue, 0);
  const costs = years.reduce((sum, year) => sum + year.annualCosts, 0);
  const profit = revenue - costs;
  const roi = costs ? (profit / costs) * 100 : 0;
  years[0].roi = years[0].annualCosts ? (years[0].profit / years[0].annualCosts) * 100 : 0;
  const firstTwoCosts = years[0].annualCosts + years[1].annualCosts;
  years[1].roi = firstTwoCosts ? ((years[0].profit + years[1].profit) / firstTwoCosts) * 100 : 0;
  years[2].roi = roi;
  const ltv = growthRate ? arpu * 12 * (100 / growthRate) : 0;
  let breakEven = 'Not in 3 years';
  if (years[0].profit > 0) breakEven = 12;
  else if (years[1].profit > 0) breakEven = 12 + Math.ceil((Math.abs(years[0].profit) / years[1].profit) * 12);

  return { years, revenue, costs, profit, roi, ltv, breakEven };
}

function Field({ label, hint, error, children }) {
  return (
    <label className="roi-field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
      {error && <small className="lead-field-error" role="alert">{error}</small>}
    </label>
  );
}

function RoiCalculator() {
  const [step, setStep] = useState(0);
  const [year, setYear] = useState(0);
  const [values, setValues] = useState(initialValues);
  const [submitStatus, setSubmitStatus] = useState('');
  const [errors, setErrors] = useState({});
  const results = useMemo(() => calculate(values), [values]);
  const update = (event) => {
    const { name } = event.target;
    const value = name === 'phone' ? limitUsPhone(event.target.value) : event.target.value;
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
  };
  const next = () => setStep((current) => Math.min(4, current + 1));
  const back = () => setStep((current) => Math.max(0, current - 1));
  const submitContact = async () => {
    const nextErrors = {};
    if (!values.name.trim()) nextErrors.name = 'Please enter your name.';
    if (!values.email.trim()) nextErrors.email = 'Please enter your email address.';
    else if (!isValidEmail(values.email)) nextErrors.email = 'Please enter a valid email address.';
    if (!values.phone.trim()) nextErrors.phone = 'Please enter your phone number.';
    else if (!isValidUsPhone(values.phone)) nextErrors.phone = 'Please enter a valid 10-digit US phone number.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setSubmitStatus('required');
      return;
    }
    setSubmitStatus('sending');
    try {
      await submitLead('Mobile App ROI Calculator', { ...values });
      setSubmitStatus('success');
      next();
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <section className="roi-section" aria-labelledby="roi-heading">
      <header className="roi-heading">
        <h2 id="roi-heading"><span>Calculate The ROI</span> Of A Mobile App Development Project</h2>
        <p>Every dollar you invest in us counts. Measure ROI for your upcoming app development project in just a few minutes.</p>
      </header>

      <div className="roi-shell">
        <nav className="roi-steps" aria-label="ROI calculator progress">
          {steps.map((label, index) => (
            <button key={label} type="button" className={index === step ? 'active' : index < step ? 'done' : ''} onClick={() => index <= step && setStep(index)}>
              <i>{index < step ? '✓' : index + 1}</i><span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="roi-panel">
          {step === 0 && <CalculatorStep eyebrow="Step 1 of 5 · Development Costs" title="Development Costs">
            <div className="roi-form-grid">
              <Field label="Initial Development Cost ($)" hint="One-time cost to build your app"><input name="developmentCost" type="number" min="0" value={values.developmentCost} onChange={update} placeholder="e.g. 50000" /></Field>
              <Field label="Monthly Maintenance Cost ($)" hint="Servers, updates, support per month"><input name="maintenanceCost" type="number" min="0" value={values.maintenanceCost} onChange={update} placeholder="e.g. 2000" /></Field>
              <Field label="App Complexity" hint="Scope & features level"><select name="complexity" value={values.complexity} onChange={update}><option value="" disabled>Select App Complexity</option><option value="simple">Simple</option><option value="moderate">Moderate</option><option value="complex">Complex</option></select></Field>
              <Field label="Platform"><select name="platform" value={values.platform} onChange={update}><option value="" disabled>Select Platform</option><option value="both">Both (iOS &amp; Android)</option><option value="ios">iOS Only</option><option value="android">Android Only</option></select></Field>
            </div>
            <Actions onNext={next} />
          </CalculatorStep>}

          {step === 1 && <CalculatorStep eyebrow="Step 2 of 5 · Revenue Model" title="Revenue Model">
            <div className="roi-form-grid">
              <Field label="Monetization Strategy"><select name="monetization" value={values.monetization} onChange={update}><option value="" disabled>Select Monetization Strategy</option><option value="subscription">Subscription</option><option value="premium">Premium</option><option value="inapp">In-App Purchases</option><option value="ads">Ads</option><option value="paid">Paid App</option></select></Field>
              <Field label="Expected Monthly Active Users (Year 1)"><input name="users" type="number" min="0" value={values.users} onChange={update} placeholder="e.g. 5000" /></Field>
              <Field label="Average Revenue Per User (ARPU) – $/month"><input name="arpu" type="number" min="0" step="0.01" value={values.arpu} onChange={update} placeholder="e.g. 4.99" /></Field>
              <Field label="User Growth Rate (% per year)"><input name="growth" type="number" min="0" value={values.growth} onChange={update} placeholder="e.g. 50" /></Field>
            </div>
            <Actions onBack={back} onNext={next} />
          </CalculatorStep>}

          {step === 2 && <CalculatorStep eyebrow="Step 3 of 5 · Marketing & Operations" title="Marketing & Operations">
            <div className="roi-form-grid">
              <Field label="Monthly Marketing Budget ($)" hint="Ads, ASO, content etc."><input name="marketing" type="number" min="0" value={values.marketing} onChange={update} placeholder="e.g. 3000" /></Field>
              <Field label="User Acquisition Cost (CAC) – $/user"><input name="cac" type="number" min="0" step="0.01" value={values.cac} onChange={update} placeholder="e.g. 2.50" /></Field>
              <Field label="User Retention Rate (%)"><input name="retention" type="number" min="0" max="100" value={values.retention} onChange={update} placeholder="e.g. 40" /></Field>
              <Field label="Conversion Rate (%)"><input name="conversion" type="number" min="0" max="100" value={values.conversion} onChange={update} placeholder="e.g. 3" /></Field>
            </div>
            <p className="roi-note">Core formulas use development, maintenance, marketing, users, ARPU, retention and growth.</p>
            <Actions onBack={back} onNext={next} nextLabel="Calculate ROI" />
          </CalculatorStep>}

          {step === 3 && <CalculatorStep eyebrow="Step 4 of 5 · Your Contact Details" title="Your Contact Details" intro="We’ll send you a copy of this 3-year ROI summary.">
            <div className="roi-form-grid roi-contact-grid">
              <Field label="Your Name" error={errors.name}><input name="name" type="text" value={values.name} onChange={update} placeholder="Your Name" aria-invalid={Boolean(errors.name)} /></Field>
              <Field label="Email" error={errors.email}><input name="email" type="email" value={values.email} onChange={update} placeholder="Your Email" aria-invalid={Boolean(errors.email)} /></Field>
            </div>
            <div className="roi-form-grid roi-contact-single"><Field label="Phone" error={errors.phone}><input name="phone" type="tel" inputMode="numeric" maxLength="14" value={values.phone} onChange={update} placeholder="(631) 895-5761" aria-invalid={Boolean(errors.phone)} /></Field></div>
            {submitStatus === 'error' && <p className="roi-note" role="alert">Could not send your information. Please try again.</p>}
            <Actions onBack={back} onNext={submitContact} nextLabel={submitStatus === 'sending' ? 'Sending...' : 'View Results'} />
          </CalculatorStep>}

          {step === 4 && <Results results={results} year={year} setYear={setYear} onBack={back} onReset={() => { setValues(initialValues); setStep(0); }} />}
        </div>
      </div>
    </section>
  );
}

function CalculatorStep({ eyebrow, title, intro, children }) {
  return <div className="roi-step-view"><small className="roi-eyebrow">{eyebrow}</small><h3>{title}</h3>{intro && <p className="roi-intro">{intro}</p>}{children}</div>;
}

function Actions({ onBack, onNext, nextLabel = 'Next' }) {
  const ArrowIcon = ({ reverse = false }) => (
    <svg className={reverse ? 'is-reverse' : ''} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14M14 7l5 5-5 5" />
    </svg>
  );

  return <div className="roi-actions">
    {onBack && <button type="button" className="secondary" onClick={onBack}><ArrowIcon reverse /><span>Back</span></button>}
    <button type="button" onClick={onNext}><span>{nextLabel}</span><ArrowIcon /></button>
  </div>;
}

function Results({ results, year, setYear, onBack, onReset }) {
  const selected = results.years[year];
  const max = Math.max(...results.years.flatMap((item) => [item.annualRevenue, Math.max(0, item.profit)]), 1);
  const revenuePoints = results.years.map((item, index) => `${30 + index * 220},${190 - (item.annualRevenue / max) * 145}`).join(' ');
  const costPoints = results.years.map((item, index) => `${30 + index * 220},${190 - (item.annualCosts / max) * 145}`).join(' ');
  const profitPoints = results.years.map((item, index) => `${30 + index * 220},${190 - (Math.max(0, item.profit) / max) * 145}`).join(' ');
  return <div className="roi-results">
    <small className="roi-eyebrow">Step 5 of 5 · Results</small><h3>3-Year ROI Results</h3>
    <div className="roi-result-cards">
      <ResultCard label="Total 3-Year ROI" value={`${percent.format(results.roi)}%`} detail="Return on Investment" />
      <ResultCard label="Break-Even Point" value={typeof results.breakEven === 'number' ? `${results.breakEven} mo` : results.breakEven} detail="Time to profitability" />
      <ResultCard label="Total Profit" value={money.format(results.profit)} detail="3-year net profit" />
      <ResultCard label="Lifetime Value" value={money.format(results.ltv)} detail="LTV per user (est.)" />
    </div>
    <div className="roi-data-grid">
      <article className="roi-chart"><header><h4>3-Year Financial Projection</h4><span>Revenue · Costs · Profit</span></header><svg viewBox="0 0 500 220" role="img" aria-label="Three year financial projection"><path d="M30 22v168h445M30 60h445M30 105h445M30 150h445" /><polyline className="revenue-line" points={revenuePoints} /><polyline className="cost-line" points={costPoints} /><polyline className="profit-line" points={profitPoints} />{results.years.map((item, index) => <circle key={index} cx={30 + index * 220} cy={190 - (item.annualRevenue / max) * 145} r="5" />)}</svg></article>
      <article className="roi-breakdown"><header><h4>Year-by-Year Breakdown</h4><div>{[0, 1, 2].map((item) => <button type="button" className={year === item ? 'active' : ''} onClick={() => setYear(item)} key={item}>Year {item + 1}</button>)}</div></header><dl><div><dt>Active Users</dt><dd>{number.format(selected.activeUsers)}</dd></div><div><dt>Monthly Revenue</dt><dd>{money.format(selected.monthlyRevenue)}</dd></div><div><dt>Annual Revenue</dt><dd>{money.format(selected.annualRevenue)}</dd></div><div><dt>Annual Costs</dt><dd>{money.format(selected.annualCosts)}</dd></div><div><dt>Net Profit</dt><dd>{money.format(selected.profit)}</dd></div><div><dt>ROI</dt><dd>{percent.format(selected.roi)}%</dd></div></dl></article>
    </div>
    <article className="roi-totals"><header><h4>3-Year Totals</h4><span>Total ROI: {percent.format(results.roi)}%</span></header><dl><div><dt>Revenue</dt><dd>{money.format(results.revenue)}</dd></div><div><dt>Costs</dt><dd>{money.format(results.costs)}</dd></div><div><dt>Net Profit</dt><dd>{money.format(results.profit)}</dd></div><div><dt>ROI</dt><dd>{percent.format(results.roi)}%</dd></div><div><dt>Break-Even</dt><dd>{typeof results.breakEven === 'number' ? `${results.breakEven} months` : results.breakEven}</dd></div></dl></article>
    <article className="roi-summary-preview"><strong>Summary preview</strong><p>{`A ${money.format(results.costs)} estimated investment projects ${money.format(results.revenue)} in revenue and ${money.format(results.profit)} net profit over three years.`}</p></article>
    <aside className="roi-ready"><h3>Ready to Build Your App?</h3><p>Our expert team can discuss your project and provide a detailed quote.</p><div><Link to="/connect">Schedule Free Consultation</Link><button type="button" onClick={onReset}>Calculate Another Project</button></div></aside>
    <div className="roi-actions"><button type="button" className="secondary" onClick={onBack}>← Back</button></div>
  </div>;
}

function ResultCard({ label, value, detail }) {
  return <article><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}

export default RoiCalculator;
